import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '../Button';
import useStyles from './styles';
import { createInvoice } from '../../lib/payments/bitpay';
import BitPayLogo from './BitPayLogo';
import { useTheme } from '@mui/material/styles';

/**
 * Button to handle opening BitPay modal and returning Invoice ID.
 */
const BitPayButton: React.FC<BitPayButtonProps> = (props) => {
  const { apiUri, cost, email, disabled, handleComplete } = props;
  const { classes } = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [bitPayModalOpen, setBitPayModalOpen] = React.useState(false);
  const bitPayIsPaid = React.useRef(false);

  const checkInvoicePaid = React.useCallback((event: MessageEvent<any>) => {
    const payment_status = event.data.status;
    if (payment_status === 'paid') {
      bitPayIsPaid.current = true;
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('message', checkInvoicePaid);
    return () => window.removeEventListener('message', checkInvoicePaid);
  }, [checkInvoicePaid]);

  const handleBitPayButton = () => {
    setBitPayModalOpen(true);
    openBitPayModal(email, cost / 100);
  };

  const openBitPayModal = async (email: string, price: number) => {
    if (window && window.bitpay) {
      const invoiceId = await createInvoice(apiUri, email, price);
      const { bitpay } = window;
      // Set up modal close handlers
      bitpay.onModalWillLeave(() => {
        if (bitPayIsPaid.current) {
          setBitPayModalOpen(false);
          handleComplete(invoiceId);
        } else {
          setBitPayModalOpen(false);
        }
      });
      if (process.env.NODE_ENV === 'development') {
        console.log('BitPay test mode enabled');
        bitpay.enableTestMode();
      }
      // Open modal
      bitpay.showInvoice(invoiceId);
    }
  };

  return (
    <Button
      variant={'contained'}
      onClick={handleBitPayButton}
      disabled={disabled || bitPayModalOpen}
      size={'large'}
      fullWidth={mobile}
    >
      {bitPayModalOpen ? (
        <CircularProgress color="inherit" size={32} />
      ) : (
        <BitPayLogo className={classes.bitpayLogo} />
      )}
    </Button>
  );
};

export default BitPayButton;

export type BitPayButtonProps = {
  apiUri: string;
  cost: number;
  email: string;
  disabled: boolean;
  handleComplete: (invoiceId: string) => void;
};
